import {
  ViewListIcon,
  PeopleIcon,
  ReceiptLongIcon,
  AddBoxIcon,
  DomainAddIcon,
  PersonAddIcon,
  PostAddIcon,
  InventoryIcon,
  BrokenImageIcon,
  PaymentIcon,
  CurrencyRupeeIcon,
  DescriptionIcon,
  ListAltIcon,
  MoveDownIcon,
  CalendarMonthIcon,
  LogoutTwoTone,
  LoginTwoTone,
  ReceiptIcon,
  AccountBalanceIcon,
  CategoryTwoToneIcon,
  AssessmentIcon,
  SummarizeIcon,
  BusinessIcon,
  StoreIcon,
  SettingsIcon,
  GroupAddIcon,
  RestoreIcon,
  ContactSupportIcon,
  LogoutIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material'
import ApplicationLinks from '../ApplicationLinks'

export type MenuItemType = 'navigation' | 'action' | 'logout'

export interface MenuItem {
  id: string
  label: string
  icon: any
  type: MenuItemType
  category: string
  keyboardShortcut?: string
  route?: () => string
  actionId?: string // For actions that trigger dialogs
  /** Optional search aliases shown in command palette as Label (syn1, syn2, …) */
  synonyms?: string[]
}

/** Command palette row text when synonyms exist */
export function getCommandPaletteDisplayLabel(item: MenuItem): string {
  if (item.synonyms?.length) {
    return `${item.label} (${item.synonyms.join(', ')})`
  }
  return item.label
}

/** Case-insensitive match on label or any synonym substring */
export function menuItemMatchesSearchQuery(item: MenuItem, queryLower: string): boolean {
  if (item.label.toLowerCase().includes(queryLower)) return true
  return (item.synonyms ?? []).some((s) => s.trim().toLowerCase().includes(queryLower))
}

export const menuItems: MenuItem[] = [
  // Sites & Parties
  {
    id: 'sites',
    label: 'Sites',
    icon: ViewListIcon,
    type: 'navigation',
    category: 'Sites & Parties',
    keyboardShortcut: 'Alt+F2',
    route: ApplicationLinks.currentAccountLink,
  },
  {
    id: 'parties',
    label: 'Parties',
    icon: PeopleIcon,
    type: 'navigation',
    category: 'Sites & Parties',
    keyboardShortcut: 'Alt+F3',
    route: ApplicationLinks.customerLink,
  },
  {
    id: 'quotations',
    label: 'Quotations',
    icon: ReceiptLongIcon,
    type: 'navigation',
    category: 'Sites & Parties',
    route: ApplicationLinks.quotationsLink,
  },
  {
    id: 'agreements',
    label: 'Agreements',
    icon: ReceiptLongIcon,
    type: 'navigation',
    category: 'Sites & Parties',
    route: ApplicationLinks.agreementsLink,
  },

  // Core Actions
  {
    id: 'new-quotation',
    label: 'New Quotation',
    icon: AddBoxIcon,
    type: 'navigation',
    category: 'Core Actions',
    route: ApplicationLinks.newQuotationLink,
  },
  {
    id: 'add-site',
    label: 'Add Site',
    icon: DomainAddIcon,
    type: 'action',
    category: 'Core Actions',
    keyboardShortcut: 'F2',
    actionId: 'openNewSite',
  },
  {
    id: 'add-party',
    label: 'Add Party',
    icon: PersonAddIcon,
    type: 'action',
    category: 'Core Actions',
    keyboardShortcut: 'F3',
    actionId: 'showAddCustomer',
  },
  {
    id: 'issue-items',
    label: 'Issue Items',
    icon: PostAddIcon,
    type: 'action',
    category: 'Core Actions',
    keyboardShortcut: 'F4',
    actionId: 'createOutwardChallan',
    synonyms: ['Delivery Challan'],
  },
  {
    id: 'receive-items',
    label: 'Receive Items',
    icon: InventoryIcon,
    type: 'action',
    category: 'Core Actions',
    keyboardShortcut: 'F5',
    actionId: 'createInwardChallan',
    synonyms: ['Receiving Challan'],
  },
  {
    id: 'receive-excess-quantity',
    label: 'Receive Excess Oty',
    icon: InventoryIcon,
    type: 'action',
    category: 'Core Actions',
    actionId: 'showReceiveExcess',
    synonyms: ['Extra Quantity'],
  },
  {
    id: 'lost-damaged-items',
    label: 'Lost/Damaged Items',
    icon: BrokenImageIcon,
    type: 'action',
    category: 'Core Actions',
    keyboardShortcut: 'F6',
    actionId: 'receiveLost',
    synonyms: ['Breakage'],
  },
  {
    id: 'receive-payment',
    label: 'Receive Payment',
    icon: PaymentIcon,
    type: 'action',
    category: 'Core Actions',
    keyboardShortcut: 'F8',
    actionId: 'showAddPayment',
    synonyms: ['Receive', 'Refund', 'TDS'],
  },
  {
    id: 'add-other-charges',
    label: 'Add Other Charges',
    icon: CurrencyRupeeIcon,
    type: 'action',
    category: 'Core Actions',
    keyboardShortcut: 'F9',
    actionId: 'showOtherCharge',
    synonyms: ['Transport etc'],
  },
  {
    id: 'create-invoice',
    label: 'Create Invoice',
    icon: DescriptionIcon,
    type: 'action',
    category: 'Core Actions',
    keyboardShortcut: 'F10',
    actionId: 'showCreateMonthlyBill',
    synonyms: ['Create Bill'],
  },

  // Secondary Actions
  {
    id: 'create-bulk-invoices',
    label: 'Create Bulk Invoices',
    icon: ListAltIcon,
    type: 'navigation',
    category: 'Secondary Actions',
    route: ApplicationLinks.recentBillsLink,
    synonyms: ['Multiple Bills'],
  },
  {
    id: 'site-transfer',
    label: 'Site To Site Transfer',
    icon: MoveDownIcon,
    type: 'action',
    category: 'Secondary Actions',
    actionId: 'siteTransfer',
  },

  // Important Lists
  {
    id: 'daily-register',
    label: 'Daily Register',
    icon: CalendarMonthIcon,
    type: 'navigation',
    category: 'Important Lists',
    keyboardShortcut: 'Alt+D',
    route: () => ApplicationLinks.slipsByDateLink('DATE'),
  },
  {
    id: 'outward-challan-register',
    label: 'Delivery Challan Register',
    icon: LogoutTwoTone,
    type: 'navigation',
    category: 'Important Lists',
    keyboardShortcut: 'Alt+F4',
    route: () => ApplicationLinks.allChallansLink('OUT'),
  },
  {
    id: 'inward-challan-register',
    label: 'Receiving Challan Register',
    icon: LoginTwoTone,
    type: 'navigation',
    category: 'Important Lists',
    keyboardShortcut: 'Alt+F5',
    route: () => ApplicationLinks.allChallansLink('IN'),
  },
  {
    id: 'invoice-register',
    label: 'Invoice Register',
    icon: ReceiptIcon,
    type: 'navigation',
    category: 'Important Lists',
    keyboardShortcut: 'Alt+F10',
    route: ApplicationLinks.allBillsLink,
  },
  {
    id: 'payment-register',
    label: 'Payment Register',
    icon: PaymentIcon,
    type: 'navigation',
    category: 'Important Lists',
    route: ApplicationLinks.paymentHistoryLink,
  },
  // Sale
  {
    id: 'create-sale-invoice',
    label: 'Create Sale Invoice',
    icon: DescriptionIcon,
    type: 'action',
    category: 'Sale',
    actionId: 'showCreateSaleInvoice',
  },
  {
    id: 'sale-invoices',
    label: 'Sale Invoices',
    icon: ReceiptIcon,
    type: 'navigation',
    category: 'Sale',
    route: ApplicationLinks.saleInvoicesLink,
    synonyms: ['Sale Invoice Register'],
  },
  {
    id: 'party-wise-ledger',
    label: 'Party-Wise Ledger',
    icon: AccountBalanceIcon,
    type: 'navigation',
    category: 'Important Lists',
    route: ApplicationLinks.ledgerSummaryLink,
  },
  {
    id: 'party-wise-pending-items-reports',
    label: 'Party Wise Pending Items',
    icon: AssessmentIcon,
    type: 'navigation',
    category: 'Important Lists',
    route: ApplicationLinks.sitesStockSummaryLink,
  },
  {
    id: 'month-wise-billing',
    label: 'Month-Wise Billing',
    icon: AssessmentIcon,
    type: 'navigation',
    category: 'Important Lists',
    route: ApplicationLinks.allSitesLedgerLink,
  },
  {
    id: 'month-wise-bills-payment',
    label: 'Month-Wise Bills/Payment',
    icon: AssessmentIcon,
    type: 'navigation',
    category: 'Important Lists',
    route: ApplicationLinks.billsPaymentChartLink,
  },
  {
    id: 'monthly-billing-vs-payments',
    label: 'Estimated Rent Chart',
    icon: AssessmentIcon,
    type: 'navigation',
    category: 'Important Lists',
    route: ApplicationLinks.billingChartLink,
  },

  // Inventory Management
  {
    id: 'vendors',
    label: 'Vendors',
    icon: PeopleIcon,
    type: 'navigation',
    category: 'Inventory Management',
    route: ApplicationLinks.vendorsLink,
  },
  {
    id: 'add-stock',
    label: 'Add Stock',
    icon: AddBoxIcon,
    type: 'action',
    category: 'Inventory Management',
    actionId: 'showAddStock',
  },
  {
    id: 'stock-transactions',
    label: 'Stock Transactions',
    icon: ReceiptIcon,
    type: 'navigation',
    category: 'Inventory Management',
    route: ApplicationLinks.stockTransactionsLink,
  },
  {
    id: 'godown-stock',
    label: 'Godown Stock',
    icon: CategoryTwoToneIcon,
    type: 'navigation',
    category: 'Inventory Management',
    keyboardShortcut: 'Alt+K',
    route: ApplicationLinks.stockItemCategoryLink,
  },
  {
    id: 'party-wise-pending-items-inventory',
    label: 'Party Wise Pending Items',
    icon: AssessmentIcon,
    type: 'navigation',
    category: 'Inventory Management',
    keyboardShortcut: 'Alt+B',
    route: ApplicationLinks.sitesStockSummaryLink,
  },

  // Analytics & Reports (GST Filing section in sidebar)
  {
    id: 'gstr1-report',
    label: 'GSTR1 Report',
    icon: AssessmentIcon,
    type: 'navigation',
    category: 'Analytics & Reports',
    route: ApplicationLinks.gstr1SummaryLink,
  },
  {
    id: 'gstr3b-report',
    label: 'GSTR 3B Report',
    icon: SummarizeIcon,
    type: 'navigation',
    category: 'Analytics & Reports',
    route: ApplicationLinks.gstr3bLink,
  },

  // Settings & Administration
  {
    id: 'business-profile',
    label: 'Business Profile',
    icon: BusinessIcon,
    type: 'navigation',
    category: 'Settings & Administration',
    keyboardShortcut: 'Alt+P',
    route: ApplicationLinks.profileDetailLink,
  },
  {
    id: 'invoice-settings',
    label: 'Invoice Settings',
    icon: StoreIcon,
    type: 'navigation',
    category: 'Settings & Administration',
    route: ApplicationLinks.invoiceTnCLink,
  },
  {
    id: 'import-data',
    label: 'Import Data',
    icon: FileUploadIcon,
    type: 'navigation',
    category: 'Settings & Administration',
    route: ApplicationLinks.importDataLink,
  },
  {
    id: 'software-settings',
    label: 'Software Settings',
    icon: SettingsIcon,
    type: 'navigation',
    category: 'Settings & Administration',
    keyboardShortcut: 'Alt+S',
    route: ApplicationLinks.profileSettingsLink,
  },
  {
    id: 'users-management',
    label: 'Users Management',
    icon: GroupAddIcon,
    type: 'navigation',
    category: 'Settings & Administration',
    route: ApplicationLinks.staffLink,
  },
  {
    id: 'switch-account',
    label: 'Switch Account',
    icon: StoreIcon,
    type: 'navigation',
    category: 'Settings & Administration',
    route: ApplicationLinks.storeChangeLink,
  },

  // System & Support
  {
    id: 'software-usage-bills',
    label: 'Software Usage Bills',
    icon: ReceiptIcon,
    type: 'navigation',
    category: 'System & Support',
    route: ApplicationLinks.pendingBillsLink,
  },
  {
    id: 'purchase-activate-plan',
    label: 'Purchase/Activate Plan',
    icon: ReceiptIcon,
    type: 'navigation',
    category: 'System & Support',
    route: ApplicationLinks.subscriptionPlansLink,
  },
  {
    id: 'manage-backups',
    label: 'Manage Backups',
    icon: RestoreIcon,
    type: 'navigation',
    category: 'System & Support',
    route: ApplicationLinks.restoreBackup,
  },
  {
    id: 'contact-support',
    label: 'Contact Support',
    icon: ContactSupportIcon,
    type: 'navigation',
    category: 'System & Support',
    route: ApplicationLinks.contactUsLink,
  },
  {
    id: 'logout',
    label: 'Logout',
    icon: LogoutIcon,
    type: 'logout',
    category: 'System & Support',
  },
]

